import { useState, useCallback, type FormEvent } from 'react';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/contexts/AuthContext';
import { useApiQuery } from '@/hooks/useApiQuery';

interface UserOption { id: number; full_name: string; }

interface AddCompanyModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddCompanyModal({ open, onClose, onSuccess }: AddCompanyModalProps) {
  const { post } = useApi();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';

  const { data: usersData } = useApiQuery<{ data?: UserOption[] } | UserOption[]>({
    endpoint: '/users',
    enabled: open && isSuperAdmin,
  });
  const usersList = Array.isArray(usersData) ? usersData : (usersData as { data?: UserOption[] } | null)?.data || [];

  const { data: companyTypesData } = useApiQuery<{ id: number; name: string }[]>({
    endpoint: '/masters/company-types',
    enabled: open,
  });
  const companyTypes = companyTypesData || [];

  const { data: docCategoriesData } = useApiQuery<{ id: number; name: string }[]>({
    endpoint: '/masters/document-categories',
    enabled: open,
  });
  const docCategories = docCategoriesData || [];

  const [form, setForm] = useState({
    name: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    primaryColor: '#d97706',
    secondaryColor: '#f59e0b',
    managedBy: '',
    companyTypeId: '',
    requiredDocuments: [] as string[],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setForm({
      name: '',
      contactEmail: '',
      contactPhone: '',
      address: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      primaryColor: '#d97706',
      secondaryColor: '#f59e0b',
      managedBy: '',
      companyTypeId: '',
      requiredDocuments: [],
    });
    setError(null);
    setLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const updateField = useCallback(
    (field: string, value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const toggleRequiredDoc = useCallback((name: string) => {
    setForm((prev) => {
      const has = prev.requiredDocuments.includes(name);
      return {
        ...prev,
        requiredDocuments: has
          ? prev.requiredDocuments.filter((d) => d !== name)
          : [...prev.requiredDocuments, name],
      };
    });
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);
      setLoading(true);

      try {
        const body: Record<string, unknown> = { name: form.name.trim() };
        if (form.contactEmail.trim()) body.contactEmail = form.contactEmail.trim();
        if (form.contactPhone.trim()) body.contactPhone = form.contactPhone.trim();
        if (form.address.trim()) body.address = form.address.trim();
        if (form.city.trim()) body.city = form.city.trim();
        if (form.state.trim()) body.state = form.state.trim();
        if (form.country.trim()) body.country = form.country.trim();
        if (form.postalCode.trim()) body.postalCode = form.postalCode.trim();
        if (form.primaryColor.trim()) body.primaryColor = form.primaryColor.trim();
        if (form.secondaryColor.trim()) body.secondaryColor = form.secondaryColor.trim();
        if (isSuperAdmin && form.managedBy) body.managedBy = Number(form.managedBy);
        if (form.companyTypeId) body.companyTypeId = Number(form.companyTypeId);
        if (form.requiredDocuments.length > 0) body.requiredDocuments = form.requiredDocuments;

        await post('/companies', body);
        reset();
        onSuccess();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create company');
      } finally {
        setLoading(false);
      }
    },
    [form, isSuperAdmin, post, reset, onSuccess]
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
          <h2 className="text-lg font-bold text-stone-900">Add Company</h2>
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1">Company Name</label>
            <input
              required
              type="text"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
              placeholder="Acme Corp"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Contact Email</label>
              <input
                type="email"
                value={form.contactEmail}
                onChange={(e) => updateField('contactEmail', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                placeholder="contact@acme.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Contact Phone</label>
              <input
                type="tel"
                value={form.contactPhone}
                onChange={(e) => updateField('contactPhone', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                placeholder="+1 234 567 8900"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1">Address</label>
            <input
              type="text"
              value={form.address}
              onChange={(e) => updateField('address', e.target.value)}
              className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
              placeholder="123 Main Street"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">City</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => updateField('city', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                placeholder="New York"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">State / Province</label>
              <input
                type="text"
                value={form.state}
                onChange={(e) => updateField('state', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                placeholder="NY"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Country</label>
              <input
                type="text"
                value={form.country}
                onChange={(e) => updateField('country', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                placeholder="United States"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Postal Code</label>
              <input
                type="text"
                value={form.postalCode}
                onChange={(e) => updateField('postalCode', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                placeholder="10001"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Primary Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.primaryColor}
                  onChange={(e) => updateField('primaryColor', e.target.value)}
                  className="w-10 h-10 rounded-lg border border-stone-200 cursor-pointer shrink-0"
                />
                <input
                  type="text"
                  value={form.primaryColor}
                  onChange={(e) => updateField('primaryColor', e.target.value)}
                  className="flex-1 border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 uppercase"
                  placeholder="#d97706"
                  maxLength={7}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Secondary Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.secondaryColor}
                  onChange={(e) => updateField('secondaryColor', e.target.value)}
                  className="w-10 h-10 rounded-lg border border-stone-200 cursor-pointer shrink-0"
                />
                <input
                  type="text"
                  value={form.secondaryColor}
                  onChange={(e) => updateField('secondaryColor', e.target.value)}
                  className="flex-1 border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 uppercase"
                  placeholder="#f59e0b"
                  maxLength={7}
                />
              </div>
            </div>
          </div>

          {isSuperAdmin && (
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Managed By</label>
              <select
                value={form.managedBy}
                onChange={(e) => updateField('managedBy', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
              >
                <option value="">Unassigned</option>
                {usersList.map((u: UserOption) => (
                  <option key={u.id} value={u.id}>{u.full_name || `User #${u.id}`}</option>
                ))}
              </select>
              <p className="text-stone-400 text-xs mt-1">Assign a staff member to manage this company profile.</p>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1">Company Type</label>
            <select
              value={form.companyTypeId}
              onChange={(e) => updateField('companyTypeId', e.target.value)}
              className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
            >
              <option value="">Select a type</option>
              {companyTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          {docCategories.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-2">Required Documents for Customers</label>
              <p className="text-stone-400 text-xs mb-3">Tick which documents customers of this company must upload before activation.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {docCategories.map((cat) => {
                  const checked = form.requiredDocuments.includes(cat.name);
                  return (
                    <label
                      key={cat.id}
                      className={`flex items-center gap-2 border rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${
                        checked ? 'border-brand-300 bg-brand-50/40' : 'border-stone-200 hover:border-stone-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRequiredDoc(cat.name)}
                        className="w-4 h-4 rounded border-stone-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      />
                      <span className={`text-sm ${checked ? 'text-stone-900 font-medium' : 'text-stone-600'}`}>{cat.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2.5 rounded-full text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-brand-600 hover:bg-brand-700 disabled:bg-stone-300 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <i className="ri-add-line" />
                  Add Company
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}